import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Receipt,
  Banknote,
  Wrench,
  ShieldOff,
  ArrowRight,
  Activity,
  CalendarRange,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import FinancialsLock from "@/components/FinancialsLock";
import FinancialsPrivacyToggle from "@/components/FinancialsPrivacyToggle";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

/**
 * Bigger sibling of the dashboard's KPI cards.
 *
 * Lives on its own page (left-side `FINANCIALS` nav item) so the dashboard
 * stays a clean action surface and the owner can come here when they
 * actually want to dig into the numbers.
 */
function MetricCard({ testid, isDark, accent, icon: Icon, label, value, sub, deltaPct, href }) {
  const accents = {
    blue: { bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-600", bar: "from-blue-500/80 to-blue-500/0" },
    emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-600", bar: "from-emerald-500/80 to-emerald-500/0" },
    amber: { bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-600", bar: "from-amber-500/80 to-amber-500/0" },
    purple: { bg: "bg-purple-50 dark:bg-purple-900/20", text: "text-purple-600", bar: "from-purple-500/80 to-purple-500/0" },
    rose: { bg: "bg-rose-50 dark:bg-rose-900/20", text: "text-rose-600", bar: "from-rose-500/80 to-rose-500/0" },
    indigo: { bg: "bg-indigo-50 dark:bg-indigo-900/20", text: "text-indigo-600", bar: "from-indigo-500/80 to-indigo-500/0" },
  }[accent || "blue"];
  const inner = (
    <Card
      className={`relative overflow-hidden transition-all hover:shadow-md ${
        isDark ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-slate-200 hover:border-slate-300"
      }`}
      data-testid={testid}
    >
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accents.bar}`} />
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className={`text-xs uppercase tracking-wider font-semibold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {label}
            </p>
            <p className={`text-3xl font-bold tabular-nums ${isDark ? "text-white" : "text-slate-900"}`}>
              {value}
            </p>
            {sub && <div className="pt-1">{sub}</div>}
          </div>
          <div className={`p-2.5 rounded-lg ${accents.bg} ${accents.text}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {deltaPct !== undefined && deltaPct !== null && (
          <p
            className={`mt-3 text-xs flex items-center gap-1 ${
              deltaPct >= 0 ? "text-emerald-600" : "text-rose-500"
            }`}
            data-testid={`${testid}-delta`}
          >
            {deltaPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {deltaPct >= 0 ? "↑" : "↓"} {Math.abs(deltaPct)}% vs prior 30d
          </p>
        )}
      </CardContent>
    </Card>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

// Available date ranges. Each one returns a [startMs, endMs] window so
// we can filter the trend array AND derive the in-range revenue/profit
// totals without touching the backend. Keeping it client-side keeps
// the page snappy when the user toggles between ranges.
const DATE_RANGES = [
  { key: "30d", label: "Last 30d", days: 30 },
  { key: "7d", label: "This week", days: 7 },
  { key: "month", label: "This month", days: null }, // current calendar month
  { key: "quarter", label: "This quarter", days: null },
  { key: "ytd", label: "Year to date", days: null },
  { key: "all", label: "All time", days: null },
];

function getRangeWindow(rangeKey) {
  const now = new Date();
  const end = now.getTime();
  if (rangeKey === "all") return [0, end];
  if (rangeKey === "30d") return [end - 30 * 86400000, end];
  if (rangeKey === "7d") return [end - 7 * 86400000, end];
  if (rangeKey === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    return [d, end];
  }
  if (rangeKey === "quarter") {
    const q = Math.floor(now.getMonth() / 3) * 3;
    const d = new Date(now.getFullYear(), q, 1).getTime();
    return [d, end];
  }
  if (rangeKey === "ytd") {
    const d = new Date(now.getFullYear(), 0, 1).getTime();
    return [d, end];
  }
  return [end - 30 * 86400000, end];
}

function FinancialsBody() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const isDark = theme === "dark";
  const [insights, setInsights] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");

  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;

  useEffect(() => {
    if (!businessSlug) return;
    let cancelled = false;
    (async () => {
      try {
        // Use allSettled so a flaky aux call doesn't blank the page.
        const [insightsRes, statsRes] = await Promise.allSettled([
          axios.get(`${API}/dashboard/insights`),
          axios.get(`${API}/dashboard/stats`),
        ]);
        if (cancelled) return;
        if (insightsRes.status === "fulfilled") setInsights(insightsRes.value.data);
        if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute filtered trend + range revenue BEFORE the early return so
  // the hook count stays stable across loading transitions (rules of hooks).
  const [winStart, winEnd] = getRangeWindow(range);
  const trend = useMemo(
    () => (insights?.trend || []).filter((p) => {
      const t = new Date(p.date).getTime();
      return t >= winStart && t <= winEnd;
    }),
    [insights, winStart, winEnd]
  );
  const rangeRevenue = useMemo(() => trend.reduce((s, p) => s + (p.revenue || 0), 0), [trend]);

  if (loading || !insights) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
        <Activity className="w-8 h-8 animate-pulse text-blue-500" />
      </div>
    );
  }

  const k = insights.kpis;

  // Derived metrics — pulled from /dashboard/stats so we don't have to
  // expand the insights schema. Tax-exempt total is the sum of revenue
  // generated on tax-exempt invoices (e.g. resale-cert customers, federal
  // contracts) — useful for filing because it's the line that doesn't
  // count toward sales tax due.
  const labor = stats?.labor || { revenue: 0, profit: 0 };
  const parts = stats?.parts || { revenue: 0, profit: 0 };
  const taxExemptTotal = stats?.revenue?.tax_exempt_total || 0;
  const taxExemptCount = stats?.revenue?.tax_exempt_invoices || 0;

  return (
    <div className={`p-4 md:p-8 min-h-screen ${isDark ? "bg-slate-900" : "bg-slate-50"} space-y-6`} data-testid="financials-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-3xl md:text-4xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            Financials
          </h1>
          <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {insights.period.this_month_label} · everything you need before tax filing
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Privacy / PIN control — top-right per user request. Lets the
              owner set a PIN, or toggle the gate on/off without losing it. */}
          <FinancialsPrivacyToggle businessSlug={businessSlug} isDark={isDark} />
          {/* Date-range selector — pills, like Stripe Dashboard */}
          <div className={`inline-flex flex-wrap items-center gap-1 p-1 rounded-xl border ${isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`} data-testid="financials-range-picker">
            <CalendarRange className={`w-4 h-4 ml-2 ${isDark ? 'text-slate-400' : 'text-slate-400'}`} />
            {DATE_RANGES.map((r) => (
              <Button
                key={r.key}
                variant="ghost"
                size="sm"
                onClick={() => setRange(r.key)}
                className={`h-8 px-3 text-xs font-medium rounded-lg ${
                  range === r.key
                    ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                    : (isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100')
                }`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {range !== "30d" && (
        <p className={`text-xs -mt-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`} data-testid="range-note">
          Revenue trend reflects <strong>{DATE_RANGES.find((r) => r.key === range)?.label}</strong> ·
          showing {fmt$(rangeRevenue)} in revenue across {trend.length} days.
          KPI cards above still show the rolling 30-day window (the underlying metric).
        </p>
      )}

      {/* PRIMARY KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <MetricCard
          testid="fin-revenue"
          isDark={isDark}
          accent="blue"
          icon={DollarSign}
          label="Revenue"
          value={fmt$(k.revenue.value)}
          deltaPct={k.revenue.delta_pct}
          href={`${basePath}/invoices`}
        />
        <MetricCard
          testid="fin-net-profit"
          isDark={isDark}
          accent="emerald"
          icon={TrendingUp}
          label="Net Profit"
          value={fmt$(k.net_profit.value)}
          sub={
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {k.net_profit.margin_pct}% margin
            </span>
          }
          deltaPct={k.net_profit.delta_pct}
          href={`${basePath}/invoices?status=paid`}
        />
        <MetricCard
          testid="fin-tax-owed"
          isDark={isDark}
          accent="purple"
          icon={Receipt}
          label="Tax Owed"
          value={fmt$(k.tax_owed.value)}
          sub={
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              filing due {k.tax_owed.filing_due}
            </span>
          }
          href={`${basePath}/reports/tax`}
        />
        <MetricCard
          testid="fin-labor-profit"
          isDark={isDark}
          accent="indigo"
          icon={Wrench}
          label="Labor Profit"
          value={fmt$(labor.profit)}
          sub={
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              from {fmt$(labor.revenue)} labor revenue · 100% margin
            </span>
          }
        />
        <MetricCard
          testid="fin-tax-exempt"
          isDark={isDark}
          accent="rose"
          icon={ShieldOff}
          label="Tax Exempt"
          value={fmt$(taxExemptTotal)}
          sub={
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {taxExemptCount} exempt invoice{taxExemptCount === 1 ? "" : "s"} · not in sales-tax base
            </span>
          }
          href={`${basePath}/invoices?status=paid`}
        />
        <MetricCard
          testid="fin-pending"
          isDark={isDark}
          accent="amber"
          icon={Banknote}
          label="Pending"
          value={fmt$(k.pending.value)}
          sub={
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {k.pending.count} unpaid invoice{k.pending.count === 1 ? "" : "s"}
            </span>
          }
          href={`${basePath}/invoices?status=pending`}
        />
      </div>

      {/* REVENUE TREND — moved here from the dashboard so the dashboard stays
          actionable and the chart lives where the owner expects financial data. */}
      <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} data-testid="financials-revenue-trend">
        <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} pb-3`}>
          <CardTitle className={`text-base flex items-center justify-between ${isDark ? "text-white" : "text-slate-800"}`}>
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              Revenue trend
            </span>
            <span className={`text-xs font-normal ${isDark ? "text-slate-400" : "text-slate-500"}`}>last 30 days</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div style={{ width: "100%", height: 320, minHeight: 320 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <AreaChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGradFin" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} opacity={0.5} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: isDark ? "#64748b" : "#94a3b8" }}
                  tickFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  axisLine={{ stroke: isDark ? "#334155" : "#e2e8f0" }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: isDark ? "#64748b" : "#94a3b8" }}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  axisLine={{ stroke: isDark ? "#334155" : "#e2e8f0" }}
                />
                <RTooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#1e293b" : "#fff",
                    borderRadius: 6,
                    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                    fontSize: 12,
                  }}
                  formatter={(v) => [fmt$(v), "Revenue"]}
                  labelFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} fill="url(#revGradFin)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* PARTS vs LABOR breakdown — secondary detail for whoever's filing taxes. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} data-testid="financials-parts-card">
          <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} pb-3`}>
            <CardTitle className={`text-base flex items-center gap-2 ${isDark ? "text-white" : "text-slate-800"}`}>
              <DollarSign className="w-4 h-4 text-blue-500" />
              Parts (inventory-tracked)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Revenue</span>
              <span className={`text-xl font-bold tabular-nums ${isDark ? "text-white" : "text-slate-900"}`}>{fmt$(parts.revenue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Profit (after COGS)</span>
              <span className="text-xl font-bold tabular-nums text-emerald-500">{fmt$(parts.profit)}</span>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"} pt-1`}>
              Includes any custom inventory-tracked categories you've added in Settings.
            </p>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} data-testid="financials-labor-card">
          <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} pb-3`}>
            <CardTitle className={`text-base flex items-center gap-2 ${isDark ? "text-white" : "text-slate-800"}`}>
              <Wrench className="w-4 h-4 text-indigo-500" />
              Labor & services
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Revenue</span>
              <span className={`text-xl font-bold tabular-nums ${isDark ? "text-white" : "text-slate-900"}`}>{fmt$(labor.revenue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Profit</span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold tabular-nums text-emerald-500">{fmt$(labor.profit)}</span>
                <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-600">100% margin</Badge>
              </div>
            </div>
            <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"} pt-1`}>
              All non-inventory categories combined (labor, diagnostic, service-call, etc).
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Footer nav — quick link to the full tax report */}
      <Link
        to={`${basePath}/reports/tax`}
        className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
          isDark ? "bg-slate-800 hover:bg-slate-700 text-white border border-slate-700" : "bg-white hover:bg-slate-50 text-slate-800 border border-slate-200"
        }`}
        data-testid="financials-to-tax-reports"
      >
        <div>
          <p className="font-semibold">Need the full tax report?</p>
          <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Filing-ready CSV/PDF exports, taxable vs non-taxable breakdown, by-jurisdiction summary.
          </p>
        </div>
        <ArrowRight className="w-5 h-5" />
      </Link>
    </div>
  );
}

/**
 * Public wrapper. We gate the actual financials behind FinancialsLock —
 * if the business has a PIN configured, the user has to enter it before
 * the body mounts. Per spec, the lock re-asks every time you leave and
 * come back (the component remounts on route change because we wrap the
 * default export, not just the body).
 */
export default function Financials() {
  const { businessSlug } = useParams();
  return (
    <FinancialsLock businessSlug={businessSlug}>
      <FinancialsBody />
    </FinancialsLock>
  );
}
